import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  TableCell,
  MenuItem,
  IconButton, Button,
  LinearProgress,
  Chip,
  Divider,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useReportDataService } from "../../services/ReportDataService";
import * as XLSX from "xlsx";

const allColumnDefs = [
  { key: "EmployeeId", label: "Employee ID", flex: 1 },
  { key: "AppVersion", label: "App Version", flex: 1 },
  { key: "Model", label: "Model", flex: 1 },
  { key: "Brand", label: "Brand", flex: 1 },
  { key: "Manufacturer", label: "Manufacturer", flex: 1 },
  { key: "SystemName", label: "System", flex: 1 },
  { key: "SystemVersion", label: "Version", flex: 1 },
  { key: "UpdatedOnFormatted", label: "Last Updated", flex: 1.5 },
  { key: "SessionStartFormatted", label: "Session Start", flex: 1.5 },
  { key: "FaceValidation", label: "Face Validation", flex: 1 },
  { key: "LocationValidation", label: "Location Validation", flex: 1 },
];

const ROW_HEIGHT = 32;

const DeviceLogList = ({ empID = "All" }) => {
  const { fetchDeviceLog } = useReportDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const listRef = useRef();

  const columnDefs = useMemo(() => {
    if (empID === "All") return allColumnDefs;
    return allColumnDefs.filter(
      (col) => col.key !== "FaceValidation" 
            && col.key !== "LocationValidation" 
            && col.key !== "SessionStartFormatted" 
            && col.key !== "EmployeeId"
    );
  }, [empID]);

  useEffect(() => {
    fetchLogs();
  }, [empID]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchDeviceLog({ employeeID: empID }, false);
      setData(res);
    } catch (err) {
      console.error("Failed to fetch device logs:", err);
    } finally {
      setLoading(false);
    }
  }, [empID]);

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      columnDefs.every(({ key }) => {
        const term = (search[key] || "").toLowerCase();
        if (!term) return true;

        const value = item[key];
        return (
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(term)
        );
      })
    );
  }, [data, search, columnDefs]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];

    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.875rem",
        }}
      >
        {columnDefs.map(({ key, flex }) => {
          const rawValue = row?.[key];
          const display =
            rawValue !== null && rawValue !== undefined
              ? String(rawValue)
              : "-";

          return (
            <Box
              key={key}
              sx={{
                flex,
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                pr: 1,
              }}
            >
              {["Yes", "No"].includes(display) ? (
                <Chip
                  label={display}
                  size="small"
                  color={display === "Yes" ? "success" : "error"}
                />
              ) : (
                display
              )}
            </Box>
          );
        })}
      </Box>
    );
  };


   const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DeviceLogs");
    XLSX.writeFile(wb, "DeviceLogs.xlsx");
  };

  return (
    <Box sx={{ p: empID === "All" ? 1 : 0 }}>
      {empID === "All" && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}> 
            <Typography variant={empID === "All" ? "h2" : "h6"}>Device Info Logs</Typography>  
              <Button variant="contained" onClick={handleExport}>
                Export to Excel
              </Button>  
            
        </Box> 
       <Divider sx={{ my: 1 }} />
        </> 
     )}   


      {loading ? (
        <LinearProgress />
      ) : (
        <Box component={Paper}>
          {/* Header Row */}
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
          </Box>

          <Box sx={{ height: empID === "All" ? "65vh" : "25vh" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  width={width}
                  itemCount={filteredData.length}
                  itemSize={() => ROW_HEIGHT}
                  itemKey={(index) => filteredData[index].EmployeeId || index}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DeviceLogList;